import {defineStore} from 'pinia';
import {
  computed,
  ref,
} from 'vue';
import {useAxios} from '@/plugins/Axios/axios';

export enum GalleryMode {
  AUTOMATIC = 'automatic',
  RESTRICTED = 'restricted',
}

export type LogoPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type TransitionStyle = 'carousel' | 'flyback' | 'flyin';

export interface GeneralConfig {
  mode: GalleryMode;
  delayMinutes: number;
  timeBetweenPhotos: number;
  transitionStyle: TransitionStyle;
  logo: string | null;
  logoPosition: LogoPosition;
  backgroundImage: string | null;
  backgroundColor: string;
  accentColor: string;
  backgroundVignette: boolean;
  animateBackground: boolean;
}

export interface Configuration {
  general: GeneralConfig;
}

const defaultConfiguration: Configuration = {
  general: {
    mode: GalleryMode.AUTOMATIC,
    delayMinutes: 0,
    timeBetweenPhotos: 5,
    transitionStyle: 'flyin',
    logo: null,
    logoPosition: 'bottom-right',
    backgroundImage: null,
    backgroundVignette: true,
    backgroundColor: '#1a1a1a',
    accentColor: '#3b82f6',
    animateBackground: false,
  },
};

export const useConfigStore = defineStore('config', () => {
  const configuration = ref<Configuration | null>(null);
  const projectCode = ref<string | null>(null);
  const isLoading = ref(false);
  const isLoaded = ref(false);

  const config = computed(() => configuration.value ?? defaultConfiguration);
  const general = computed(() => config.value.general);
  const mode = computed(() => general.value.mode);
  const delayMinutes = computed(() => general.value.delayMinutes);
  const timeBetweenPhotos = computed(() => general.value.timeBetweenPhotos);
  const transitionStyle = computed(() => general.value.transitionStyle);
  const logo = computed(() => general.value.logo);
  const logoPosition = computed(() => general.value.logoPosition);
  const backgroundImage = computed(() => general.value.backgroundImage);
  const backgroundColor = computed(() => general.value.backgroundColor);
  const backgroundVignette = computed(() => general.value.backgroundVignette ?? true);
  const accentColor = computed(() => general.value.accentColor);
  const animateBackground = computed(() => general.value.animateBackground);

  function setProjectCode(code: string) {
    projectCode.value = code;
  }

  function getProjectCodeFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('projectCode') || urlParams.get('code');
  }

  async function load() {
    if (!projectCode.value) {
      const codeFromUrl = getProjectCodeFromUrl();
      if (codeFromUrl) {
        projectCode.value = codeFromUrl;
      } else {
        console.error('Project code not found in URL. Add ?projectCode=YOUR_CODE to the URL.');
        return;
      }
    }

    isLoading.value = true;
    try {
      await loadConfiguration();
      isLoaded.value = true;
    } finally {
      isLoading.value = false;
    }
  }

  async function loadConfiguration() {
    if (!projectCode.value) {
      throw new Error('Project code not set');
    }

    try {
      const axios = useAxios();
      const {data} = await axios.get(`/api/v1/external/project/by-code/${projectCode.value}/config`);

      configuration.value = data.configuration as Configuration;
    } catch (e) {
      console.error('Failed to load configuration:', e);
      configuration.value = {
        ...defaultConfiguration,
      };
    }
  }

  return {
    configuration,
    projectCode,
    isLoading,
    isLoaded,
    config,
    general,
    mode,
    delayMinutes,
    timeBetweenPhotos,
    transitionStyle,
    logo,
    logoPosition,
    backgroundImage,
    backgroundColor,
    backgroundVignette,
    accentColor,
    animateBackground,
    setProjectCode,
    getProjectCodeFromUrl,
    load,
    loadConfiguration,
  };
});
